import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Alert,
    alpha,
    Avatar,
    Box,
    Button,
    Chip,
    Container,
    Divider,
    Fade,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import { updateTeacherSelfProfile } from "../../redux/userRelated/userHandle";
import { underControl } from "../../redux/userRelated/userSlice";
import Popup from "../../components/Popup";
import { isTemporaryTeacherEmail, teacherNeedsOnboarding } from "../../utils/teacherOnboarding";
import { getTeacherSubjects } from "../../utils/teacherSubjects";
import { useFormTeacherHomeroom } from "../../hooks/useFormTeacherHomeroom";
import { useHomeroomTeacherTerm } from "../../hooks/useHomeroomTeacherTerm";
import { getStaffRoleLabel, getStaffRoleFromUser, isHomeroomTeacherRole } from "../../utils/staffRoles";
import { PASSWORD_MIN_HELPER, validatePasswordIfProvided } from "../../utils/passwordValidation";
import ClassOutlinedIcon from "@mui/icons-material/ClassOutlined";

const CONTRACT_TYPES = [
    { value: "", label: "Select contract type" },
    { value: "Government", label: "Government" },
    { value: "Private", label: "Private" },
    { value: "Contract", label: "Contract / other" },
];

const GENDER_OPTIONS = [
    { value: "", label: "Select gender" },
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Other", label: "Other" },
    { value: "Prefer not to say", label: "Prefer not to say" },
];

function initialsFromName(name) {
    if (!name || typeof name !== "string") return "?";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function TabPanel({ children, value, index }) {
    if (value !== index) return null;
    return <Box sx={{ pt: 2 }}>{children}</Box>;
}

const TeacherProfile = () => {
    const dispatch = useDispatch();
    const { currentUser, loading } = useSelector((state) => state.user);

    const [tab, setTab] = useState(0);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [dateOfBirth, setDateOfBirth] = useState("");
    const [gender, setGender] = useState("");
    const [nationalId, setNationalId] = useState("");
    const [physicalAddress, setPhysicalAddress] = useState("");
    const [emergencyName, setEmergencyName] = useState("");
    const [emergencyPhone, setEmergencyPhone] = useState("");
    const [password, setPassword] = useState("");

    const [tscNumber, setTscNumber] = useState("");
    const [qualifications, setQualifications] = useState("");
    const [contractType, setContractType] = useState("");

    const [bankName, setBankName] = useState("");
    const [bankBranch, setBankBranch] = useState("");
    const [accountNumber, setAccountNumber] = useState("");
    const [accountHolderName, setAccountHolderName] = useState("");

    const [showPopup, setShowPopup] = useState(false);
    const [popupMessage, setPopupMessage] = useState("");
    const [popupSeverity, setPopupSeverity] = useState(undefined);
    const [pendingNextTab, setPendingNextTab] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [saving, setSaving] = useState(false);

    const teacherId = currentUser?._id ?? currentUser?.id;

    useEffect(() => {
        if (!currentUser) return;
        setName(currentUser.name || "");
        setEmail(currentUser.email || "");
        setPhone(currentUser.phone || "");
        setDateOfBirth(currentUser.dateOfBirth || "");
        setGender(currentUser.gender || "");
        setNationalId(currentUser.nationalId || "");
        setPhysicalAddress(currentUser.physicalAddress || "");
        setEmergencyName(currentUser.emergencyContactName || "");
        setEmergencyPhone(currentUser.emergencyContactPhone || "");
        setTscNumber(currentUser.tscNumber || "");
        setQualifications(currentUser.qualifications || "");
        setContractType(currentUser.contractType || "");
        setBankName(currentUser.bankName || "");
        setBankBranch(currentUser.bankBranch || "");
        setAccountNumber(currentUser.accountNumber || "");
        setAccountHolderName(currentUser.accountHolderName || "");
        setPassword("");
    }, [currentUser]);

    const assignedSubjects = useMemo(() => getTeacherSubjects(currentUser), [currentUser]);
    const { homeroomClasses, roleIsHomeroom, hasHomeroomClass } = useFormTeacherHomeroom();
    const homeroomTerm = useHomeroomTeacherTerm();
    const staffRoleLabel = getStaffRoleLabel(getStaffRoleFromUser(currentUser), currentUser?.schoolType);
    const teachSchool = currentUser?.school;
    const onboarding = teacherNeedsOnboarding(currentUser);
    const hasTemporaryEmail =
        Boolean(currentUser?.emailIsTemporary) || isTemporaryTeacherEmail(currentUser?.email);

    const displayName = currentUser?.name?.trim() || "Teacher";
    const initials = useMemo(() => initialsFromName(displayName), [displayName]);

    const personalFields = () => ({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        dateOfBirth: dateOfBirth.trim(),
        gender: gender.trim(),
        nationalId: nationalId.trim(),
        physicalAddress: physicalAddress.trim(),
        emergencyContactName: emergencyName.trim() || null,
        emergencyContactPhone: emergencyPhone.trim() || null,
    });

    const academicFields = () => ({
        tscNumber: tscNumber.trim() || null,
        qualifications: qualifications.trim() || null,
        contractType: contractType || null,
    });

    const bankingFields = () => ({
        bankName: bankName.trim(),
        bankBranch: bankBranch.trim(),
        accountNumber: accountNumber.trim(),
        accountHolderName: accountHolderName.trim(),
    });

    const saveProfileFields = async (fields) => {
        if (!teacherId) {
            setPopupMessage("Could not identify your account. Sign out and sign in again.");
            setPopupSeverity("error");
            setShowPopup(true);
            return { ok: false };
        }
        setSaving(true);
        setSaveSuccess(false);
        dispatch(underControl());
        const outcome = await dispatch(updateTeacherSelfProfile(fields, teacherId));
        setSaving(false);
        return outcome;
    };

    const promptProceedToStep = (nextTab, message) => {
        setPendingNextTab(nextTab);
        setPopupMessage(message);
        setPopupSeverity("success");
        setShowPopup(true);
    };

    const handlePopupDismiss = () => {
        setShowPopup(false);
        if (pendingNextTab != null) {
            setTab(pendingNextTab);
            setPendingNextTab(null);
        }
    };

    const submitHandler = async (event) => {
        event.preventDefault();
        if (!teacherId) {
            setPopupMessage("Could not identify your account. Sign out and sign in again.");
            setPopupSeverity("error");
            setShowPopup(true);
            return;
        }

        const trimmedEmail = email.trim();
        if (!trimmedEmail || isTemporaryTeacherEmail(trimmedEmail)) {
            setTab(0);
            setPopupMessage("Enter your personal email address on the Personal tab. Temporary login emails cannot be kept.");
            setPopupSeverity("error");
            setShowPopup(true);
            return;
        }
        if (!name.trim()) {
            setTab(0);
            setPopupMessage("Enter your full name on the Personal tab.");
            setPopupSeverity("error");
            setShowPopup(true);
            return;
        }

        const passwordFields = {};
        if (password.trim()) {
            const passwordErr = validatePasswordIfProvided(password);
            if (passwordErr) {
                setPopupMessage(passwordErr);
                setPopupSeverity("error");
                setShowPopup(true);
                return;
            }
            passwordFields.password = password.trim();
        }

        if (onboarding) {
            if (tab === 0) {
                const outcome = await saveProfileFields({
                    ...personalFields(),
                    ...passwordFields,
                });
                if (outcome?.ok) {
                    setPassword("");
                    promptProceedToStep(
                        1,
                        "Personal details saved successfully.\n\nProceed to the Academic step to continue your profile setup.",
                    );
                } else {
                    setPopupMessage(outcome?.message || "Could not save profile");
                    setPopupSeverity("error");
                    setShowPopup(true);
                }
                return;
            }
            if (tab === 1) {
                const outcome = await saveProfileFields(academicFields());
                if (outcome?.ok) {
                    promptProceedToStep(
                        2,
                        "Academic details saved successfully.\n\nProceed to the Banking step to finish your profile setup.",
                    );
                } else {
                    setPopupMessage(outcome?.message || "Could not save profile");
                    setPopupSeverity("error");
                    setShowPopup(true);
                }
                return;
            }
        }

        const fields = {
            ...personalFields(),
            ...academicFields(),
            ...bankingFields(),
            ...passwordFields,
        };
        const outcome = await saveProfileFields(fields);
        if (outcome?.ok) {
            setSaveSuccess(true);
            setPassword("");
            if (onboarding && !teacherNeedsOnboarding(outcome.data)) {
                promptProceedToStep(
                    null,
                    "Profile setup complete. You can now use the teacher portal.",
                );
            }
            return;
        }
        setPopupMessage(outcome?.message || "Could not save profile");
        setPopupSeverity("error");
        setShowPopup(true);
    };

    if (!currentUser) {
        return (
            <Container sx={{ py: 6 }}>
                <Alert severity="info">Sign in to view your teacher profile.</Alert>
            </Container>
        );
    }

    return (
        <Box
            component="main"
            sx={{
                minHeight: "100%",
                pb: 6,
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.04)} 0%, ${t.palette.grey[100]} 26%)`
                        : alpha(t.palette.common.black, 0.2),
            }}
        >
            <Container maxWidth="md" sx={{ pt: { xs: 2, sm: 3 } }}>
                <Fade in timeout={360}>
                    <Stack spacing={3}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2.5, sm: 3 },
                                borderRadius: 3,
                                color: "common.white",
                                background: (t) =>
                                    `linear-gradient(120deg, ${t.palette.grey[900]} 0%, ${t.palette.primary.dark} 50%, ${t.palette.primary.main} 100%)`,
                            }}
                        >
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5} alignItems={{ sm: "center" }}>
                                <Avatar
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        fontSize: "1.5rem",
                                        fontWeight: 800,
                                        bgcolor: alpha("#fff", 0.16),
                                    }}
                                >
                                    {initials}
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="overline" sx={{ opacity: 0.9 }}>
                                        My profile
                                    </Typography>
                                    <Typography variant="h4" fontWeight={800}>
                                        {displayName}
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                                        <Chip
                                            label={staffRoleLabel}
                                            size="small"
                                            color={isHomeroomTeacherRole(getStaffRoleFromUser(currentUser)) ? "warning" : "default"}
                                            sx={{ bgcolor: alpha("#fff", 0.14), color: "inherit", fontWeight: 700 }}
                                        />
                                        {teachSchool?.schoolName ? (
                                            <Chip
                                                icon={<SchoolOutlinedIcon sx={{ color: "inherit !important" }} />}
                                                label={teachSchool.schoolName}
                                                size="small"
                                                sx={{ bgcolor: alpha("#fff", 0.14), color: "inherit" }}
                                            />
                                        ) : null}
                                        {roleIsHomeroom && hasHomeroomClass
                                            ? homeroomClasses.map((c) => (
                                                  <Chip
                                                      key={c._id || c.id}
                                                      icon={<ClassOutlinedIcon sx={{ color: "inherit !important" }} />}
                                                      label={`${homeroomTerm.chipPrefix}: ${c.sclassName}`}
                                                      size="small"
                                                      sx={{ bgcolor: alpha("#fff", 0.14), color: "inherit" }}
                                                  />
                                              ))
                                            : null}
                                        {roleIsHomeroom && !hasHomeroomClass ? (
                                            <Chip
                                                label={`${homeroomTerm.roleTitleCaps} — no class assigned yet`}
                                                size="small"
                                                color="warning"
                                                sx={{ bgcolor: alpha("#fff", 0.14), color: "inherit" }}
                                            />
                                        ) : null}
                                        {assignedSubjects.map((subject) => (
                                            <Chip
                                                key={subject._id || subject.id}
                                                icon={<MenuBookOutlinedIcon sx={{ color: "inherit !important" }} />}
                                                label={`${subject.subName}${subject.sclassName?.sclassName ? ` · ${subject.sclassName.sclassName}` : ""}`}
                                                size="small"
                                                sx={{ bgcolor: alpha("#fff", 0.14), color: "inherit" }}
                                            />
                                        ))}
                                        {onboarding ? (
                                            <Chip label="Setup required" size="small" color="warning" />
                                        ) : (
                                            <Chip label="Profile complete" size="small" color="success" />
                                        )}
                                    </Stack>
                                </Box>
                                <Button
                                    component={RouterLink}
                                    to="/logout"
                                    variant="outlined"
                                    color="inherit"
                                    startIcon={<LogoutOutlinedIcon />}
                                    sx={{ borderColor: alpha("#fff", 0.55), color: "common.white" }}
                                >
                                    Sign out
                                </Button>
                            </Stack>
                        </Paper>

                        {onboarding && (
                            <Alert severity="warning" sx={{ borderRadius: 2 }}>
                                Complete your profile before using the teacher portal. You must set a personal email address (not the temporary login email), plus personal, academic, and banking details.
                            </Alert>
                        )}

                        {hasTemporaryEmail && (
                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                                You signed in with your temporary login email: <strong>{currentUser.email}</strong>. Enter your personal email below — after saving, use that new address (and your password) to sign in next time.
                            </Alert>
                        )}

                        {saveSuccess && (
                            <Alert severity="success" onClose={() => setSaveSuccess(false)} sx={{ borderRadius: 2 }}>
                                Profile saved successfully.
                            </Alert>
                        )}

                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: 3,
                                border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                                overflow: "hidden",
                            }}
                        >
                            <Box sx={{ px: 2, pt: 1, bgcolor: (t) => alpha(t.palette.primary.main, 0.04) }}>
                                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                    {roleIsHomeroom
                                        ? hasHomeroomClass
                                            ? `As ${homeroomTerm.theRole} you can enroll pupils (pending director verification) and submit class results for the class(es) above. You also use the same Subject teaching menu as other teachers for marks, schemes, and lesson plans.`
                                            : `You are a ${homeroomTerm.theRole}. Ask your director to link you to a class under All classes.`
                                        : "Class and subject are assigned by your administrator."}
                                </Typography>
                            </Box>
                            <Divider />
                            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2 }}>
                                <Tab icon={<AccountCircleOutlinedIcon />} iconPosition="start" label="Personal" />
                                <Tab icon={<WorkOutlineOutlinedIcon />} iconPosition="start" label="Academic" />
                                <Tab icon={<AccountBalanceOutlinedIcon />} iconPosition="start" label="Banking" />
                            </Tabs>
                            <Divider />

                            <Box component="form" onSubmit={submitHandler} sx={{ px: { xs: 2, sm: 3 }, pb: 3 }}>
                                <TabPanel value={tab} index={0}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField fullWidth required label="Full name" value={name} onChange={(e) => setName(e.target.value)} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                required
                                                type="email"
                                                label={hasTemporaryEmail ? "Personal email (required)" : "Email"}
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                error={hasTemporaryEmail && isTemporaryTeacherEmail(email)}
                                                helperText={
                                                    hasTemporaryEmail
                                                        ? "Replace the temporary login address with your real email."
                                                        : undefined
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField fullWidth label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                type="date"
                                                label="Date of birth"
                                                InputLabelProps={{ shrink: true }}
                                                value={dateOfBirth}
                                                onChange={(e) => setDateOfBirth(e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Gender</InputLabel>
                                                <Select label="Gender" value={gender} onChange={(e) => setGender(e.target.value)}>
                                                    {GENDER_OPTIONS.map((opt) => (
                                                        <MenuItem key={opt.value || "none"} value={opt.value}>
                                                            {opt.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="National ID / passport"
                                                value={nationalId}
                                                onChange={(e) => setNationalId(e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                minRows={2}
                                                label="Physical address"
                                                value={physicalAddress}
                                                onChange={(e) => setPhysicalAddress(e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Emergency contact name"
                                                value={emergencyName}
                                                onChange={(e) => setEmergencyName(e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Emergency contact phone"
                                                value={emergencyPhone}
                                                onChange={(e) => setEmergencyPhone(e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                type="password"
                                                label="New password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                helperText={`Optional — leave blank to keep current. ${PASSWORD_MIN_HELPER} if changing.`}
                                                inputProps={{ minLength: 8 }}
                                            />
                                        </Grid>
                                    </Grid>
                                </TabPanel>

                                <TabPanel value={tab} index={1}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="TCM number"
                                                value={tscNumber}
                                                onChange={(e) => setTscNumber(e.target.value)}
                                                helperText="Teachers Council of Malawi registration number"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Contract type</InputLabel>
                                                <Select
                                                    label="Contract type"
                                                    value={contractType}
                                                    onChange={(e) => setContractType(e.target.value)}
                                                >
                                                    {CONTRACT_TYPES.map((opt) => (
                                                        <MenuItem key={opt.value || "none"} value={opt.value}>
                                                            {opt.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                minRows={3}
                                                label="Qualifications"
                                                placeholder="Degrees, certifications, teaching registration…"
                                                value={qualifications}
                                                onChange={(e) => setQualifications(e.target.value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </TabPanel>

                                <TabPanel value={tab} index={2}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Bank name"
                                                value={bankName}
                                                onChange={(e) => setBankName(e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Branch"
                                                value={bankBranch}
                                                onChange={(e) => setBankBranch(e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Account number"
                                                value={accountNumber}
                                                onChange={(e) => setAccountNumber(e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Account holder name"
                                                value={accountHolderName}
                                                onChange={(e) => setAccountHolderName(e.target.value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </TabPanel>

                                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        size="large"
                                        disabled={saving || loading}
                                        startIcon={<SaveOutlinedIcon />}
                                    >
                                        {saving || loading
                                            ? "Saving…"
                                            : onboarding
                                              ? tab === 0
                                                ? "Save & continue to Academic"
                                                : tab === 1
                                                  ? "Save & continue to Banking"
                                                  : "Save profile"
                                              : "Save profile"}
                                    </Button>
                                </Stack>
                            </Box>
                        </Paper>
                    </Stack>
                </Fade>
            </Container>
            <Popup
                message={popupMessage}
                setShowPopup={(open) => {
                    if (!open) handlePopupDismiss();
                    else setShowPopup(true);
                }}
                showPopup={showPopup}
                severity={popupSeverity}
            />
        </Box>
    );
};

export default TeacherProfile;
